import { useEffect, useId, useState } from "react";
import { Alert, FormControlLabel, Stack, Switch, Typography } from "@mui/material";

import { Disclose } from "@lopie/kernel-ui";

import { refusalWords } from "./sandboxPanel.js";
import { activityLine, estimateRows, noticeSwitch, upkeepLine } from "./upkeep.js";

const HELD = { overflowWrap: "anywhere", minWidth: 0 };

/**
 * The author's own switch for the emails about their test copies (LO-200), read and
 * changed through `/notices`. One hook for both places that draw the switch (the chat
 * page's panel and the app page's Test copy), so the two cannot disagree about what the
 * switch holds or how a refusal of it is said.
 */
export function useNotices(get, post, enabled) {
  const [prefs, setPrefs] = useState(null);
  const [failure, setFailure] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    get("/notices")
      .then((body) => { if (live) setPrefs(body); })
      .catch((problem) => { if (live) setFailure(problem); });
    return () => { live = false; };
  }, [get, enabled]);

  const flip = async (on) => {
    setSaving(true);
    setFailure(null);
    try {
      setPrefs(await post("/notices", { kind: "sandbox", on }));
    } catch (problem) {
      setFailure(problem);
    } finally {
      setSaving(false);
    }
  };

  return { prefs, failure, saving, flip };
}

/**
 * How old the sandbox is, what it is estimated to cost, and when it last saw
 * activity (LO-198); and the author's own switch for the emails about it (LO-200).
 *
 * The figures are the kernel's (`sandbox_upkeep` on the request). The estimate's
 * basis rides on the figure as its `title` and its lines are one click in, so the
 * qualification sits on the number it qualifies rather than in a block of its own.
 */
export default function SandboxUpkeep({ upkeep, get, post }) {
  const line = upkeepLine(upkeep);
  const { prefs, failure: prefsFailure, saving, flip } = useNotices(get, post, Boolean(line));
  const labelId = useId();

  if (!line) return null;
  const sw = noticeSwitch(prefs, "sandbox");

  return (
    <Stack spacing={0.75} component="section" aria-label="Age and estimated cost">
      <Typography variant="body2" title={line.title} sx={HELD}>{line.text}</Typography>
      <Typography variant="caption" color="text.secondary" sx={HELD}>{activityLine(upkeep)}</Typography>
      <Disclose label="How the estimate is made" align="row"
        summary={`${upkeep.estimate.label}: list prices applied to what a sandbox is built from, not a bill.`}
        count={estimateRows(upkeep.estimate).length}>
        <Stack spacing={0.5}>
          {estimateRows(upkeep.estimate).map((r) => (
            <Typography key={r.key} variant="body2" color="text.secondary" sx={HELD}>
              {r.label}: {r.figure}. {r.basis}.
            </Typography>
          ))}
          {(upkeep.estimate.does_not_prove || []).map((t, i) => (
            <Typography key={`n${i}`} variant="caption" color="text.secondary" sx={HELD}>{t}</Typography>
          ))}
        </Stack>
      </Disclose>
      {sw ? (
        <FormControlLabel
          control={<Switch size="small" checked={sw.on} disabled={saving}
            onChange={(e) => flip(e.target.checked)} slotProps={{ input: { "aria-describedby": labelId } }} />}
          label={<Typography variant="body2">{sw.on ? "Emailing you about this sandbox: on" : "Emailing you about this sandbox: off"}</Typography>}
        />
      ) : null}
      {sw ? (
        <Typography id={labelId} variant="caption" color="text.secondary" sx={HELD}>
          {sw.detail}. This changes only your own emails, for every draft you make.
        </Typography>
      ) : null}
      {prefsFailure ? (
        <Alert severity="info" variant="outlined">
          Your email setting could not be read or changed: {refusalWords(prefsFailure).text || "no answer"}.
        </Alert>
      ) : null}
    </Stack>
  );
}
